export type CalculatorInput = {
  currentShares: string | number;
  currentTotalCost: string | number;
  currentPrice: string | number;
  fxRate: string | number;
  addCash: string | number;
  buyPrice: string | number;
  buyFee: string | number;
  targetAverage: string | number;
  targetBuyPrice: string | number;
  maxLossPercent: string | number;
  targetSellPrice: string | number;
  sellPercent: string | number;
};

const numeric = (value: string | number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export function calculate(input: CalculatorInput) {
  const shares = numeric(input.currentShares);
  const oldCost = numeric(input.currentTotalCost);
  const price = numeric(input.currentPrice);
  const cash = Math.max(0, numeric(input.addCash));
  const fee = Math.max(0, numeric(input.buyFee));
  const buyPrice = Math.max(0.000001, numeric(input.buyPrice));
  const oldAverage = shares ? oldCost / shares : 0;
  const oldValue = shares * price;
  const oldPnl = oldValue - oldCost;
  const cashForShares = Math.max(0, cash - fee);
  const addedShares = cashForShares / buyPrice;
  const newShares = shares + addedShares;
  const newCost = oldCost + cash;
  const newAverage = newShares ? newCost / newShares : 0;
  const newValue = newShares * price;
  const newPnl = newValue - newCost;
  const newPnlPercent = newCost ? (newPnl / newCost) * 100 : 0;

  const targetAverage = numeric(input.targetAverage);
  const targetBuyPrice = Math.max(0.000001, numeric(input.targetBuyPrice));
  let requiredCash = 0;
  let requiredShares = 0;
  let targetReachable = true;
  if (shares > 0 && oldCost > 0 && targetAverage < oldAverage) {
    if (targetBuyPrice >= targetAverage) targetReachable = false;
    else {
      requiredShares = (oldCost - targetAverage * shares) / (targetAverage - targetBuyPrice);
      requiredCash = requiredShares * targetBuyPrice;
    }
  }

  const maxLoss = Math.min(90, Math.max(0, numeric(input.maxLossPercent))) / 100;
  const stopPrice = newShares ? (newCost * (1 - maxLoss)) / newShares : 0;
  const sellFraction = Math.min(100, Math.max(0, numeric(input.sellPercent))) / 100;
  const sharesToSell = newShares * sellFraction;
  const targetSellPrice = numeric(input.targetSellPrice);
  const targetCash = sharesToSell * targetSellPrice;
  const targetSellPnl = sharesToSell * (targetSellPrice - newAverage);
  const remainingShares = newShares - sharesToSell;

  return {
    shares, oldCost, price, cash, fee, buyPrice, oldAverage, oldValue, oldPnl,
    cashForShares, addedShares, newShares, newCost, newAverage, newValue, newPnl,
    newPnlPercent, requiredCash, requiredShares, targetReachable, stopPrice,
    sharesToSell, targetCash, targetSellPnl, remainingShares, fxRate: numeric(input.fxRate),
  };
}

export function scenarioPrices(price: number, newAverage: number, target: number, stop: number): number[] {
  const center = Math.max(1, Math.round(price / 10) * 10);
  const generated = [-30, -20, -15, -10, -5, 0, 5, 10, 15, 20, 30, 40, 50].map((pct) => Math.max(0.01, center * (1 + pct / 100)));
  return [...generated, price, newAverage, target, stop]
    .filter((value) => value > 0)
    .map((value) => Math.round(value * 100) / 100)
    .filter((value, index, values) => values.indexOf(value) === index)
    .sort((left, right) => left - right);
}
